export const BILLING_POWER_FACTOR = 0.9989;
export const DEFAULT_UNIVERSAL_SUBSIDY_LABEL = 'Universal subsidy';

export const resolveUniversalSubsidyLabel = (financeInputs) => {
  const label = String(financeInputs?.universalSubsidyLabel || '').trim();
  return label || DEFAULT_UNIVERSAL_SUBSIDY_LABEL;
};

export const toBillingNumber = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return 0;
    const parsed = Number(trimmed.replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const safeBillingDiv = (numerator, denominator) => {
  const normalizedDenominator = toBillingNumber(denominator);
  if (!normalizedDenominator) return 0;
  return toBillingNumber(numerator) / normalizedDenominator;
};

export const asNegativeBillingAmount = (value) => {
  const normalized = toBillingNumber(value);
  if (!normalized) return 0;
  return -Math.abs(normalized);
};

export const createDefaultBillingAnalyticsInputs = () => ({
  voltage: '',
  cdKva: '',
  actualCdKva: '',
  billedCdKva: '',
  unitsConsumedUnits: '',
  unitsConsumedKvah: '',
  demandChargeInr: '',
  demandChargeRateF13: '',
  energyChargeRateF15: '',
  todInr: '',
  facInr: '',
  edInr: '',
  taxOnSaleInr: '',
  tcsInr: '0',
  bulkConsumptionRebateInr: '',
  promptPaymentDiscountInr: '',
  loadFactorIncentiveInr: '',
  incrementalConsumptionRebateInr: '',
  regionalSubsidyInr: '',
  universalSubsidyInr: '',
  ebTodRateJ16: '',
  ebTaxOnSaleRateJ19: '',
  ebTcsRateJ20: '0',
  ebTcsAmountI20: '0',
  ebBulkConsumptionRebateAmountI21: '0',
  ebLoadFactorIncentiveAmountI23: '0',
  ebIncrementalConsumptionRebateAmountI24: '0',
  generationTariffInrPerUnit: '',
  wasCalculated: false,
  todTariffsBySlot: {},
  batteryTodTariffsByBlock: {},
});

export const extractPersistableBillingAnalyticsInputs = (raw, { omitKeys = [] } = {}) => {
  const defaults = createDefaultBillingAnalyticsInputs();
  const pick = (key) => raw?.[key] ?? defaults[key] ?? '';
  const result = {
    voltage: pick('voltage'),
    cdKva: pick('cdKva'),
    actualCdKva: pick('actualCdKva'),
    billedCdKva: pick('billedCdKva'),
    unitsConsumedUnits: pick('unitsConsumedUnits'),
    demandChargeInr: pick('demandChargeInr'),
    demandChargeRateF13: pick('demandChargeRateF13'),
    energyChargeRateF15: pick('energyChargeRateF15'),
    todInr: pick('todInr'),
    facInr: pick('facInr'),
    edInr: pick('edInr'),
    taxOnSaleInr: pick('taxOnSaleInr'),
    tcsInr: pick('tcsInr'),
    bulkConsumptionRebateInr: pick('bulkConsumptionRebateInr'),
    promptPaymentDiscountInr: pick('promptPaymentDiscountInr'),
    loadFactorIncentiveInr: pick('loadFactorIncentiveInr'),
    incrementalConsumptionRebateInr: pick('incrementalConsumptionRebateInr'),
    regionalSubsidyInr: pick('regionalSubsidyInr'),
    universalSubsidyInr: pick('universalSubsidyInr'),
    ebTodRateJ16: pick('ebTodRateJ16'),
    ebTaxOnSaleRateJ19: pick('ebTaxOnSaleRateJ19'),
    ebTcsRateJ20: pick('ebTcsRateJ20'),
    ebTcsAmountI20: pick('ebTcsAmountI20'),
    ebBulkConsumptionRebateAmountI21: pick('ebBulkConsumptionRebateAmountI21'),
    ebLoadFactorIncentiveAmountI23: pick('ebLoadFactorIncentiveAmountI23'),
    ebIncrementalConsumptionRebateAmountI24: pick('ebIncrementalConsumptionRebateAmountI24'),
    generationTariffInrPerUnit: pick('generationTariffInrPerUnit'),
    wasCalculated: Boolean(raw?.wasCalculated),
    todTariffsBySlot: raw?.todTariffsBySlot && typeof raw.todTariffsBySlot === 'object' ? raw.todTariffsBySlot : {},
    batteryTodTariffsByBlock:
      raw?.batteryTodTariffsByBlock && typeof raw.batteryTodTariffsByBlock === 'object'
        ? raw.batteryTodTariffsByBlock
        : {},
  };
  omitKeys.forEach((key) => {
    delete result[key];
  });
  return result;
};

export const hasSavedBillingAnalyticsDocument = (raw) => (
  Boolean(raw) && typeof raw === 'object' && !Array.isArray(raw) && Object.keys(raw).length > 0
);

export const overlaySavedBillingInputs = (current, saved) => {
  if (!hasSavedBillingAnalyticsDocument(saved)) return current;
  const persisted = extractPersistableBillingAnalyticsInputs(saved);
  const next = { ...current };
  Object.keys(saved).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(persisted, key)) {
      next[key] = persisted[key];
    }
  });
  const derivedKvah = deriveBillingKvahFromUnits(next.unitsConsumedUnits);
  if (derivedKvah) next.unitsConsumedKvah = derivedKvah;
  return next;
};

export const deriveBillingKvahFromUnits = (units) => {
  const normalizedUnits = toBillingNumber(units);
  if (!normalizedUnits) return '';
  return String(Math.round(normalizedUnits / BILLING_POWER_FACTOR));
};

export const mapBillingFinanceInputs = (financeInputs) => {
  if (!financeInputs) return null;
  const hasValue = (value) => value !== null && value !== undefined && value !== '';
  return {
    unitsX: hasValue(financeInputs.unitsX) ? financeInputs.unitsX : null,
    energyCostA: hasValue(financeInputs.energyCostA) ? financeInputs.energyCostA : null,
    wheelingChargeB: hasValue(financeInputs.wheelingChargeB) ? financeInputs.wheelingChargeB : null,
    todChargesD: hasValue(financeInputs.todChargesD) ? financeInputs.todChargesD : null,
    facF: hasValue(financeInputs.facF) ? financeInputs.facF : null,
    electricityDutyC: hasValue(financeInputs.electricityDutyC) ? financeInputs.electricityDutyC : null,
    taxE: hasValue(financeInputs.taxE) ? financeInputs.taxE : null,
    bulkConsumptionRebate: hasValue(financeInputs.bulkConsumptionRebate) ? financeInputs.bulkConsumptionRebate : null,
    promptPaymentDiscount: hasValue(financeInputs.promptPaymentDiscount) ? financeInputs.promptPaymentDiscount : null,
    incrementalConsumptionRebate: hasValue(financeInputs.incrementalConsumptionRebate) ? financeInputs.incrementalConsumptionRebate : null,
    loadFactorIncentive: hasValue(financeInputs.loadFactorIncentive) ? financeInputs.loadFactorIncentive : null,
    universalSubsidy: hasValue(financeInputs.universalSubsidy) ? financeInputs.universalSubsidy : null,
    universalSubsidyLabel: resolveUniversalSubsidyLabel(financeInputs),
  };
};

export const applyFinanceInputsToBillingInputs = (rawInputs, financeInputs, savedInputs = null) => {
  const normalizedInputs = extractPersistableBillingAnalyticsInputs(rawInputs);
  const financeMapped = mapBillingFinanceInputs(financeInputs);
  const effectiveUnits = financeMapped?.unitsX ?? normalizedInputs.unitsConsumedUnits;
  const derivedKvah = deriveBillingKvahFromUnits(effectiveUnits);

  const financeApplied = {
    ...normalizedInputs,
    unitsConsumedUnits: financeMapped?.unitsX ?? normalizedInputs.unitsConsumedUnits,
    unitsConsumedKvah: derivedKvah || normalizedInputs.unitsConsumedKvah,
    todInr: financeMapped?.todChargesD ?? normalizedInputs.todInr,
    facInr: financeMapped?.facF ?? normalizedInputs.facInr,
    edInr: financeMapped?.electricityDutyC ?? normalizedInputs.edInr,
    taxOnSaleInr: financeMapped?.taxE ?? normalizedInputs.taxOnSaleInr,
    bulkConsumptionRebateInr:
      financeMapped?.bulkConsumptionRebate != null
        ? String(asNegativeBillingAmount(financeMapped.bulkConsumptionRebate))
        : normalizedInputs.bulkConsumptionRebateInr,
    promptPaymentDiscountInr:
      financeMapped?.promptPaymentDiscount != null
        ? String(asNegativeBillingAmount(financeMapped.promptPaymentDiscount))
        : normalizedInputs.promptPaymentDiscountInr,
    incrementalConsumptionRebateInr:
      financeMapped?.incrementalConsumptionRebate != null
        ? String(asNegativeBillingAmount(financeMapped.incrementalConsumptionRebate))
        : normalizedInputs.incrementalConsumptionRebateInr,
    loadFactorIncentiveInr:
      financeMapped?.loadFactorIncentive != null
        ? String(asNegativeBillingAmount(financeMapped.loadFactorIncentive))
        : normalizedInputs.loadFactorIncentiveInr,
    universalSubsidyInr:
      financeMapped?.universalSubsidy != null
        ? String(asNegativeBillingAmount(financeMapped.universalSubsidy))
        : normalizedInputs.universalSubsidyInr,
  };
  return overlaySavedBillingInputs(financeApplied, savedInputs);
};

export const buildBillingTodTariffModel = ({ bankingSummary = null, inputs = {} }) => {
  const fixed = Array.isArray(bankingSummary?.time_slot_summary) ? bankingSummary.time_slot_summary : [];
  const fixedData = fixed.filter((row) => String(row?.TimeSlot || '').trim().toLowerCase() !== 'total');

  if (!fixedData.length) {
    return {
      rows: [],
      totals: { totalConsumption: 0, totalDemandMet: 0, totalGridConsumption: 0, weightedAvgTariff: 0 },
      offPeakTariff: 0,
      derivedJ16: 0,
      hasData: false,
    };
  }

  const wantedSlots = ["24 to 6", "6 to 9", "9 to 17", "17 to 24"];
  const rowsByKey = {};
  fixedData.forEach((row) => {
    const key = String(row?.TimeSlot ?? '').trim();
    if (key) rowsByKey[key] = row;
  });

  const mappedRows = wantedSlots.map((key) => {
    const row = rowsByKey[key] || {};
    const consumption = toBillingNumber(row?.Consumption);
    const demandMet = toBillingNumber(row?.DemandMet);
    const gridConsumption = Math.max(0, consumption - demandMet);
    const tariff = toBillingNumber(inputs?.todTariffsBySlot?.[key]);
    return { key, label: key, consumption, demandMet, gridConsumption, tariff };
  });

  const totalConsumption = mappedRows.reduce((sum, row) => sum + toBillingNumber(row.consumption), 0);
  const totalDemandMet = mappedRows.reduce((sum, row) => sum + toBillingNumber(row.demandMet), 0);
  const totalGridConsumption = mappedRows.reduce((sum, row) => sum + toBillingNumber(row.gridConsumption), 0);
  const weightedSum = mappedRows.reduce(
    (sum, row) => sum + (toBillingNumber(row.gridConsumption) * toBillingNumber(row.tariff)),
    0
  );
  const weightedAvgTariff = totalGridConsumption ? (weightedSum / totalGridConsumption) : 0;
  const offPeakTariff = toBillingNumber(inputs?.todTariffsBySlot?.["6 to 9"] || inputs?.todTariffsBySlot?.["24 to 6"]);

  return {
    rows: mappedRows,
    totals: { totalConsumption, totalDemandMet, totalGridConsumption, weightedAvgTariff },
    offPeakTariff,
    derivedJ16: weightedAvgTariff - offPeakTariff,
    hasData: true,
  };
};

const getBillingDiscountRate = (globalParams = {}) => {
  const discountRate = toBillingNumber(globalParams?.discount_rate);
  if (!discountRate) return 0;
  return discountRate > 1 ? discountRate / 100 : discountRate;
};

const getBillingLossFraction = (value) => {
  const normalized = toBillingNumber(value);
  if (!normalized) return 0;
  return normalized > 1 ? normalized / 100 : normalized;
};

export const computeBillingAnalytics = ({
  inputs: rawInputs = {},
  financeInputs = null,
  globalParams = {},
  annualMetrics = null,
  bankingSummary = null,
  opCostsOutput = null,
  savedInputs = null,
}) => {
  const inputs = applyFinanceInputsToBillingInputs(rawInputs, financeInputs, savedInputs);
  const financeMapped = mapBillingFinanceInputs(financeInputs);
  const todTariffModel = buildBillingTodTariffModel({ bankingSummary, inputs });

  const wheelingRate = toBillingNumber(globalParams?.wheeling_charges);
  const transmissionRate = toBillingNumber(globalParams?.transmission_charges_per_KWh);
  const netSettledUnitsAnnual = toBillingNumber(annualMetrics?.unitsFromOA);
  const hourlyUnitsBanked = toBillingNumber(annualMetrics?.hourlyUnitsBanked);
  const reSuppliedUnits = toBillingNumber(annualMetrics?.reSuppliedUnits);
  const discountRate = getBillingDiscountRate(globalParams);
  const transmissionLossPerc = getBillingLossFraction(globalParams?.transmission_loss_perc);
  const wheelingLossPerc = getBillingLossFraction(globalParams?.wheeling_loss_perc);

  let lapsedPctTotal = 0;
  try {
    const rows = bankingSummary?.tod_summary || [];
    if (rows.length) {
      const totalRow = rows[rows.length - 1];
      const pct = Number(totalRow?.LapsedPct);
      if (Number.isFinite(pct)) {
        lapsedPctTotal = pct > 1 ? pct / 100 : pct;
      }
    }
  } catch {
    lapsedPctTotal = 0;
  }

  const extractOpSeries = (metricName) => {
    if (!Array.isArray(opCostsOutput) || !opCostsOutput.length) return [];
    const row = opCostsOutput.find(
      (entry) => String(entry?.Year || '').trim().toLowerCase() === String(metricName || '').trim().toLowerCase()
    );
    if (!row) return [];
    const keys = Object.keys(row)
      .filter((key) => key !== 'Year' && /^\d+$/.test(key))
      .sort((left, right) => Number(left) - Number(right));
    return keys.map((key) => toBillingNumber(row[key]));
  };

  const extractOpScalar = (metricName) => {
    if (!Array.isArray(opCostsOutput) || !opCostsOutput.length) return null;
    const row = opCostsOutput.find(
      (entry) => String(entry?.Year || '').trim().toLowerCase() === String(metricName || '').trim().toLowerCase()
    );
    if (!row) return null;
    const firstNumericKey = Object.keys(row)
      .filter((key) => key !== 'Year' && /^\d+$/.test(key))
      .sort((left, right) => Number(left) - Number(right))[0];
    if (!firstNumericKey) return null;
    return toBillingNumber(row[firstNumericKey]);
  };

  const npv = (rate, cashflows) => {
    const normalizedRate = toBillingNumber(rate);
    if (!cashflows || !cashflows.length) return 0;
    let total = 0;
    for (let index = 0; index < cashflows.length; index += 1) {
      total += toBillingNumber(cashflows[index]) / Math.pow(1 + normalizedRate, index + 1);
    }
    return total;
  };

  const npvPerUnit = (costSeries, unitSeries) => {
    const discountedCost = npv(discountRate, costSeries);
    const discountedUnits = npv(discountRate, unitSeries);
    return safeBillingDiv(discountedCost, discountedUnits);
  };

  const E = {
    voltage: inputs.voltage,
    cd: toBillingNumber(inputs.cdKva),
    actualCd: toBillingNumber(inputs.actualCdKva),
    billedCd: toBillingNumber(inputs.billedCdKva),
    units: toBillingNumber(inputs.unitsConsumedUnits),
    kvah: toBillingNumber(inputs.unitsConsumedKvah),
    demandCharge: toBillingNumber(inputs.demandChargeInr),
    demandRateF13: toBillingNumber(inputs.demandChargeRateF13),
    energyRateF15: toBillingNumber(inputs.energyChargeRateF15),
    tod: financeMapped?.todChargesD ?? toBillingNumber(inputs.todInr),
    fac: financeMapped?.facF ?? toBillingNumber(inputs.facInr),
    ed: financeMapped?.electricityDutyC ?? toBillingNumber(inputs.edInr),
    taxOnSale: financeMapped?.taxE ?? toBillingNumber(inputs.taxOnSaleInr),
    tcs: toBillingNumber(inputs.tcsInr),
    bulkRebate: asNegativeBillingAmount(financeMapped?.bulkConsumptionRebate ?? toBillingNumber(inputs.bulkConsumptionRebateInr)),
    promptDiscount: asNegativeBillingAmount(financeMapped?.promptPaymentDiscount ?? toBillingNumber(inputs.promptPaymentDiscountInr)),
    loadFactorIncentive: asNegativeBillingAmount(financeMapped?.loadFactorIncentive ?? toBillingNumber(inputs.loadFactorIncentiveInr)),
    incrRebate: asNegativeBillingAmount(financeMapped?.incrementalConsumptionRebate ?? toBillingNumber(inputs.incrementalConsumptionRebateInr)),
    regionalSubsidy: toBillingNumber(inputs.regionalSubsidyInr),
    universalSubsidy: asNegativeBillingAmount(
      financeMapped?.universalSubsidy ?? toBillingNumber(inputs.universalSubsidyInr)
    ),
    universalSubsidyLabel: resolveUniversalSubsidyLabel(financeInputs),
  };

  const F12 = safeBillingDiv(E.kvah, E.units);
  const wheelingCharge = financeMapped?.wheelingChargeB ?? (E.kvah * wheelingRate);
  const energyCharge = financeMapped?.energyCostA ?? (E.kvah * E.energyRateF15);
  const F16 = safeBillingDiv(E.tod, E.kvah);
  const F17 = safeBillingDiv(E.fac, E.kvah);
  const baseSumDemandToFac = E.demandCharge + wheelingCharge + energyCharge + E.tod + E.fac;
  const F18 = safeBillingDiv(E.ed, baseSumDemandToFac);
  const F19 = safeBillingDiv(E.taxOnSale, E.kvah);
  const F20 = safeBillingDiv(E.tcs, E.kvah);
  const F21 = safeBillingDiv(E.bulkRebate, E.units);
  const F22 = safeBillingDiv(E.promptDiscount, E.units);
  const F23 = safeBillingDiv(E.loadFactorIncentive, E.kvah);
  const F24 = safeBillingDiv(E.incrRebate, E.units);
  const F25 = safeBillingDiv(E.regionalSubsidy, E.units);
  const universalSubsidyRate = safeBillingDiv(E.universalSubsidy, E.units);

  const totalPowerCostE26 =
    E.demandCharge +
    wheelingCharge +
    energyCharge +
    E.tod +
    E.fac +
    E.ed +
    E.taxOnSale +
    E.tcs +
    E.bulkRebate +
    E.promptDiscount +
    E.loadFactorIncentive +
    E.incrRebate +
    E.regionalSubsidy +
    E.universalSubsidy;

  const F26 = safeBillingDiv(totalPowerCostE26, E.units);
  const replaceablePowerCostE27 = safeBillingDiv(totalPowerCostE26 - E.demandCharge, E.kvah);

  const generationTariff = toBillingNumber(inputs.generationTariffInrPerUnit);
  const oc = {
    transmissionCharge: transmissionRate,
    transmissionLoss: transmissionLossPerc * generationTariff,
    wheelingCharge: wheelingRate,
    wheelingLoss: wheelingLossPerc * generationTariff,
    bankingCharge: (() => {
      if (!generationTariff) return 0;
      const demandPerc = reSuppliedUnits ? (hourlyUnitsBanked / reSuppliedUnits) : 0;
      const bankPerc = 0.08 * demandPerc;
      return bankPerc * demandPerc * generationTariff;
    })(),
    lapsedUnitCharge: lapsedPctTotal * generationTariff,
    omLeaseInsuranceOther: (() => {
      const units = extractOpSeries('Net Settled Units');
      const maintenance = extractOpSeries('Annual Maintenance (in crores)');
      const lease = extractOpSeries('Lease charges (in crores)');
      const assetManagement = extractOpSeries('Asset Management Fees (in crores)');
      const statutory = extractOpSeries('Statutory Govt Charges(in crores)');
      if (!units.length) return 0;
      return (
        npvPerUnit(maintenance, units) +
        npvPerUnit(lease, units) +
        npvPerUnit(assetManagement, units) +
        npvPerUnit(statutory, units)
      );
    })(),
  };

  const deliveredPowerCostH12 =
    oc.transmissionCharge +
    oc.transmissionLoss +
    oc.wheelingCharge +
    oc.wheelingLoss +
    oc.bankingCharge +
    oc.lapsedUnitCharge +
    oc.omLeaseInsuranceOther;

  const landedCostOverallRate = extractOpScalar('Consumer Effective Levellised Cost (Landed cost)');
  const oaUnitsRateH12 = landedCostOverallRate != null ? landedCostOverallRate : deliveredPowerCostH12;

  const G12 = netSettledUnitsAnnual ? netSettledUnitsAnnual / 12 : 0;
  const G26 = G12 * oaUnitsRateH12;

  const I10 = E.billedCd;
  const I12 = E.kvah - G12;
  const I11 = I12 * F12;
  const I13 = E.demandCharge;
  const J13 = E.demandRateF13;
  const J14 = wheelingRate;
  const I14 = I12 * J14;
  const J15 = E.energyRateF15;
  const I15 = I12 * J15;
  const J16 = todTariffModel.hasData ? toBillingNumber(todTariffModel.derivedJ16) : toBillingNumber(inputs.ebTodRateJ16);
  const I16 = I12 * J16;
  const J17 = F17;
  const I17 = I12 * J17;
  const J18 = F18;
  const I18 = J18 * (I13 + I14 + I15 + I16 + I17);
  const J19 = toBillingNumber(inputs.ebTaxOnSaleRateJ19);
  const I19 = I12 * J19;
  const J20 = toBillingNumber(inputs.ebTcsRateJ20);
  const I20 = toBillingNumber(inputs.ebTcsAmountI20);
  const J21 = F21;
  const I21 = toBillingNumber(inputs.ebBulkConsumptionRebateAmountI21);
  const J22 = F22;
  const I22 = J22 * I12;
  const J23 = F23;
  const I23 = toBillingNumber(inputs.ebLoadFactorIncentiveAmountI23);
  const J24 = F24;
  const I24 = toBillingNumber(inputs.ebIncrementalConsumptionRebateAmountI24);
  const J25 = F25;
  const I25 = 0;

  const I26 = I13 + I14 + I15 + I16 + I17 + I18 + I19 + I20 + I21 + I22 + I23 + I24 + I25;
  const J26 = safeBillingDiv(I26, I11);
  const I27 = I26 + G26;
  const J27 = safeBillingDiv(I27 - I13, E.kvah);
  const J28 = replaceablePowerCostE27 - J27;
  const savingsOnOaUnits = G12 ? (J28 * E.kvah) / G12 : 0;

  return {
    inputs,
    financeMapped,
    todTariffModel,
    context: {
      discountRate,
      transmissionLossPerc,
      wheelingLossPerc,
      wheelingRate,
      transmissionRate,
      netSettledUnitsAnnual,
      hourlyUnitsBanked,
      reSuppliedUnits,
      lapsedPctTotal,
    },
    calc: {
      F12,
      wheelingCharge,
      energyCharge,
      F16,
      F17,
      F18,
      F19,
      F20,
      F21,
      F22,
      F23,
      F24,
      F25,
      universalSubsidyRate,
      universalSubsidyLabel: E.universalSubsidyLabel,
      totalPowerCostE26,
      F26,
      replaceablePowerCostE27,
      netSettledUnitsAnnual,
      G12,
      landedCostOverallRate,
      oaUnitsRateH12,
      deliveredPowerCostH12,
      G26,
      I10,
      I11,
      I12,
      I13,
      J13,
      I14,
      J14,
      I15,
      J15,
      I16,
      J16,
      I17,
      J17,
      I18,
      J18,
      I19,
      J19,
      I20,
      J20,
      I21,
      J21,
      I22,
      J22,
      I23,
      J23,
      I24,
      J24,
      I25,
      J25,
      I26,
      J26,
      I27,
      J27,
      J28,
      savingsOnOaUnits,
      oc,
      generationTariff,
      E,
    },
  };
};
